/**
 * Pure business rules for marker mode and visibility resolution.
 *
 * Why this file exists:
 * - Separates display rules from the wiring concerns in markers.plugin.js.
 * - All functions here are pure: no side effects, no lifecycle dependencies.
 * - Rules can be tested and reasoned about independently of plugin state.
 */

import MarkerMode from '../enums/markerMode';
import MarkerType from '../enums/markerType';

/**
 * Zoom level at which paid markers switch from pin to price display mode.
 */
export const PRICE_PIN_ZOOM_LEVEL = 13;

/**
 * Minimum zoom level at which the Community Interactive Site Map indicator appears on pins.
 * This can be adjusted as needed to match UX requirements.
 */
export const INTERACTIVE_SITE_MAP_ZOOM = 10;

/**
 * Minimum zoom level at which basic markers become visible.
 * Only enforced when the location is Market or City and there are more than
 * PAID_MARKERS_THRESHOLD paid results.
 */
export const BASIC_PIN_ZOOM_LEVEL = 12;

/**
 * Number of paid markers above which the zoom threshold is applied for basic
 * markers in Market/City locations.
 */
export const PAID_MARKERS_THRESHOLD = 25;

/**
 * Determines if the Community Interactive Site Map indicator should be shown for a marker.
 *
 * @param {Object} markerData - Marker data object
 * @param {number} zoom - Current map zoom level
 * @returns {boolean}
 */
export function shouldShowCommunityExplorerIndicator(markerData, zoom) {
  return (
    markerData?.type === MarkerType.HousingDevelopment.toString() &&
    markerData.showCommunityExplorer === true &&
    zoom >= INTERACTIVE_SITE_MAP_ZOOM
  );
}

/**
 * Resolves the display mode and marker decorations for a marker at the
 * current zoom and context.
 *
 * All marker display rules — including whether the Community Interactive
 * Site Map indicator should be shown — are decided here, in the feature's
 * domain. mapLibrary only applies whatever is returned; it does not know
 * about the Community Interactive Site Map.
 *
 * @param {Object} markerData
 * @param {{ zoom: number }} context
 * @returns {{ mode: string, showInteractiveSiteMap: boolean }}
 */
export function resolveMode(markerData, context) {
  const canUsePriceMode =
    context.zoom >= PRICE_PIN_ZOOM_LEVEL &&
    !!markerData.displayText;

  return {
    mode: canUsePriceMode ? MarkerMode.Price.toString() : MarkerMode.Pin.toString(),
    showInteractiveSiteMap: shouldShowCommunityExplorerIndicator(markerData, context.zoom),
  };
}

/**
 * Determines whether a basic marker should be visible given the current
 * zoom and default viewport visibility.
 *
 * The zoom threshold is only enforced when the location type is Market or City
 * AND there are more than PAID_MARKERS_THRESHOLD paid results. Otherwise,
 * basic markers are shown at any zoom level.
 *
 * @param {number} zoom - current map zoom level
 * @param {boolean} defaultVisibility - viewport-based default visibility
 * @param {{ isMarketOrCity?: boolean, paidCount?: number }} [context]
 * @returns {boolean}
 */
export function isBasicMarkerVisible(zoom, defaultVisibility, { isMarketOrCity = false, paidCount = 0 } = {}) {
  const applyThreshold = isMarketOrCity && paidCount > PAID_MARKERS_THRESHOLD;

  if (applyThreshold && zoom < BASIC_PIN_ZOOM_LEVEL) {
    return false;
  }

  return defaultVisibility;
}
